/**
 * Meeting Events Page
 * /meetings/:id/events - 모임 일정 목록 페이지
 */

import { useRoute, useLocation } from "wouter";
import { useQuery } from "@tanstack/react-query";
import { ArrowLeft, Calendar } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";

interface MeetingEvent {
  id: string;
  title: string;
  date: string;
  time: string | null;
  location: any;
  capacity: number | null;
  attendees: number;
  status: "open" | "closed";
  image: string | null;
  isPrivate?: boolean;
}

interface Meeting {
  id: string;
  title: string;
  events: MeetingEvent[];
}

export default function MeetingEventsPage() {
  const [match, params] = useRoute<{ id: string }>("/meetings/:id/events");
  const [, navigate] = useLocation();

  const meetingId = params?.id;

  // 모임 기본 정보 조회
  const { data: meetingData } = useQuery<{
    ok: boolean;
    data: { id: string; title: string };
  }>({
    queryKey: ["/api/meetings", meetingId],
    queryFn: async () => {
      if (!meetingId) throw new Error("Invalid meeting ID");
      const response = await fetch(`/api/meetings/${meetingId}`);
      if (!response.ok) throw new Error("Failed to fetch meeting");
      return response.json();
    },
    enabled: !!meetingId,
  });

  // 일정 목록 조회 (전용 API)
  const { data: eventsData, isLoading } = useQuery<{
    ok: boolean;
    data: {
      meetingId: string;
      total: number;
      events: MeetingEvent[];
    };
  }>({
    queryKey: ["/api/meetings", meetingId, "events"],
    queryFn: async () => {
      if (!meetingId) throw new Error("Invalid meeting ID");
      const response = await fetch(`/api/meetings/${meetingId}/events`);
      if (!response.ok) throw new Error("Failed to fetch events");
      const json = await response.json();

      // API 응답이 중첩 구조일 수 있음: { ok: true, data: { ok: true, data: {...} } }
      let eventsData: any = null;
      if (json.ok && json.data) {
        // 중첩된 경우 다시 확인
        if (json.data.ok && json.data.data) {
          eventsData = json.data.data;
        } else if (json.data.events) {
          // 이미 events 객체인 경우
          eventsData = json.data;
        } else {
          eventsData = json.data;
        }
      } else if (json.events) {
        // 직접 events 객체인 경우
        eventsData = json;
      }

      return { ok: true, data: eventsData || { meetingId, total: 0, events: [] } };
    },
    enabled: !!meetingId,
  });

  const meeting = meetingData?.ok ? meetingData.data : null;
  const events = eventsData?.ok && eventsData.data?.events && Array.isArray(eventsData.data.events)
    ? eventsData.data.events
    : [];

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return `${date.getFullYear()}년 ${date.getMonth() + 1}월 ${date.getDate()}일`;
  };

  const formatTime = (timeString: string | null) => {
    if (!timeString) return "";
    const [hours, minutes] = timeString.split(":");
    const hour = parseInt(hours);
    const period = hour >= 12 ? "오후" : "오전";
    const displayHour = hour > 12 ? hour - 12 : hour === 0 ? 12 : hour;
    return `${period} ${displayHour}:${minutes}`;
  };

  if (!match || !params) {
    return (
      <div className="min-h-screen bg-background py-8">
        <div className="max-w-6xl mx-auto px-4">
          <Card className="p-12 text-center">
            <p className="text-muted-foreground mb-4">
              유효하지 않은 모임 ID입니다
            </p>
            <Button variant="outline" onClick={() => navigate("/meetings")}>
              목록으로
            </Button>
          </Card>
        </div>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background py-8">
        <div className="max-w-6xl mx-auto px-4">
          <Card className="p-12 text-center">
            <p className="text-muted-foreground">로딩 중...</p>
          </Card>
        </div>
      </div>
    );
  }

  if (!meeting) {
    return (
      <div className="min-h-screen bg-background py-8">
        <div className="max-w-6xl mx-auto px-4">
          <Card className="p-12 text-center">
            <p className="text-muted-foreground mb-4">
              모임 정보를 찾을 수 없습니다
            </p>
            <Button variant="outline" onClick={() => navigate("/meetings")}>
              목록으로
            </Button>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* 헤더 */}
      <div className="sticky top-0 z-50 bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60 border-b">
        <div className="max-w-6xl mx-auto px-4 py-3 flex items-center gap-4">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate(`/meetings/${meetingId}`)}
          >
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <h1 className="text-lg font-semibold">
            {meeting.title} - 일정 ({events.length}개)
          </h1>
        </div>
      </div>

      {/* 메인 콘텐츠 */}
      <div className="max-w-6xl mx-auto px-4 py-6">
        {events.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {events.map((event) => (
              <Card
                key={event.id}
                className="overflow-hidden hover:shadow-md transition-shadow cursor-pointer"
              >
                {event.image && (
                  <div className="aspect-video bg-muted overflow-hidden">
                    <img
                      src={event.image}
                      alt={event.title}
                      className="w-full h-full object-cover"
                    />
                  </div>
                )}
                <CardContent className="p-4">
                  <div className="flex items-start justify-between mb-2">
                    <div className="flex items-center gap-2 text-sm text-muted-foreground">
                      <Calendar className="w-4 h-4" />
                      <span>{formatDate(event.date)}</span>
                    </div>
                    <Badge
                      variant={
                        event.status === "open" ? "default" : "secondary"
                      }
                      className="text-xs"
                    >
                      {event.status === "open" ? "모집중" : "마감"}
                    </Badge>
                  </div>
                  <h3 className="font-semibold mb-2 line-clamp-2">
                    {event.isPrivate
                      ? "🔒 모임에만 공개된 일정이에요."
                      : event.title}
                  </h3>
                  {event.time && (
                    <p className="text-sm text-muted-foreground mb-1">
                      ⏰ {formatTime(event.time)}
                    </p>
                  )}
                  {event.location && (
                    <p className="text-sm text-muted-foreground mb-1">
                      📍 {typeof event.location === "string" ? event.location : event.location.name || "장소 미정"}
                    </p>
                  )}
                  <p className="text-sm text-muted-foreground">
                    👥 참가자 {event.attendees}
                    {event.capacity ? ` / ${event.capacity}` : ""}명
                  </p>
                </CardContent>
              </Card>
            ))}
          </div>
        ) : (
          <Card>
            <CardContent className="p-12 text-center">
              <Calendar className="w-12 h-12 mx-auto mb-4 opacity-50 text-muted-foreground" />
              <p className="text-muted-foreground">아직 일정이 없습니다</p>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}

